import {Project, ProjectApi, ProjectState} from "@betrue/shared/interfaces";
import {useCallback, useEffect, useState} from "react";
import {gql, makeVar, useLazyQuery, useMutation, useReactiveVar} from "@apollo/client";
import {useReactiveResult, useResult} from "./results";
import {StringParam, useQueryParam} from "use-query-params";
import {BASE_ENTITY_FIELDS} from "./base-fields";
import {makeVarPersisted} from "@betrue/shared/utils";


export const initProject: Project = {
  id: undefined,
  removed: undefined,
  version: undefined,
  state: ProjectState.QUALIFIED,
  owners: [],
  name: "",
  description: "",
  dueDate: new Date(),
  selectedProducts: []
}

export const useSessionProject = (): [Project, (p: Project) => void] => {
  return [useReactiveVar<Project>(sessionProject), (project: Project) => sessionProject(project)]
}
export const CORE_PROJECT_FIELDS = gql`
  fragment CoreProjectField on ProjectModel {
    ${BASE_ENTITY_FIELDS}
    state
    dueDate
    __typename
    description
    selectedProducts {
      __typename
      id
      name
      slug
      maxPrice
      minPrice
    }
    owners {
      id
      firstName
      lastName
    }
    name
  }
`
const GET_PROJECT_BY_ID =
gql`
  ${CORE_PROJECT_FIELDS}
  query getProjectById($id: String!){
    getProjectById(id: $id){
      ...CoreProjectField
    }
  }

`

const CREATE_NEW_PROJECT =
gql`
  ${CORE_PROJECT_FIELDS}
  mutation createNewProject( $project:  ProjectModelInput!){
    createProject(project: $project ){
      ...CoreProjectField
    }
  }

`

const DELETE_PROJECT =
gql`
  ${CORE_PROJECT_FIELDS}
  mutation deleteProject( $id:  String!){
    deleteProject(id: $id ){
      ...CoreProjectField
    }
  }
`
const LIST_PROJECTS =
gql`
  ${CORE_PROJECT_FIELDS}
  query listProjects{
    projects{
      ...CoreProjectField
    }
  }

`

const UPDATE_PROJECT =
gql`
  ${CORE_PROJECT_FIELDS}
  mutation updateProject( $project: ProjectModelInput!){
    updateProject(project: $project){
      ...CoreProjectField
    }
  }
`

export const DELETE_PRODUCT = gql`
  ${CORE_PROJECT_FIELDS}
  mutation deleteProduct( $projectId: String!, $productId:String!){
    deleteProduct(projectId: $projectId,productId: $productId ){
      ...CoreProjectField
    }
  }
`

export const ADD_PRODUCT =
gql`
  ${CORE_PROJECT_FIELDS}
  mutation addProduct( $product: ProductModelInput!, $projectId: String!){
    addProduct(product: $product, projectId: $projectId ){
      ...CoreProjectField
    }
  }
`
export const currentProject = makeVar(initProject)
export const sessionProject = makeVarPersisted<Project>(initProject, 'cart')

export const useProject = (store?): [Project, Project[], ProjectApi] => {
  const [single, setSingle] = useReactiveResult<Project>(store ? store : currentProject)

  const [list, setList] = useState<Project[]>();
  const [projectId, setProjectId] = useQueryParam('projectId', StringParam);
  const projectById = useResult(useLazyQuery(GET_PROJECT_BY_ID, {fetchPolicy: "network-only"}), setSingle)
  const createProject = useResult(useMutation(CREATE_NEW_PROJECT, {}), setSingle)
  const deleteProject = useResult(useMutation(DELETE_PROJECT, {}), setSingle)
  const listProjects = useResult(useLazyQuery(LIST_PROJECTS, {fetchPolicy: "network-only"}), setList)
  const addProduct = useCallback(useResult(useMutation(ADD_PRODUCT, {}), setSingle), [])
  const deleteProduct = useCallback(useResult(useMutation(DELETE_PRODUCT, {}), setSingle), [])
  const updateProject = useResult(useMutation(UPDATE_PROJECT, {}), setSingle)

  useEffect(() => {
    if (single.id) {
      setProjectId(single.id)
    }
  }, [single])

  useEffect(() => {
    if (projectId) {
      projectById({
        variables: {
          id: projectId
        }
      })
    }
  }, [])

  return [
    single,
    list,
    {
      deleteProduct(projectId: string, productId: string) {
        return deleteProduct({
          variables: {
            projectId: projectId,
            productId: productId
          }
        })
      },
      addProduct(projectId, p) {

        return addProduct({
          variables: {
            projectId: projectId,
            product: p
          }
        })
      },
      createProject(p: Project) {
        return createProject({
          variables: {
            project: p
          }
        })
      },
      updateProject(p) {
        return updateProject({
          variables: {
            project: p
          }
        })
      }, deleteProject(id) {
        return deleteProject({
          variables: {
            id: id
          }
        })
      }, getProjectById(id: string) {
        return projectById({
          variables: {
            id: id
          }
        })
      }, projects() {
        return listProjects()
      }
    }]
}
